import React from 'react';
import {
  Button,
  Toggle,
  DatePicker,
  DatePickerInput,
  TextInput,
  Form,
  InlineLoading,
  Loading,
  NumberInput
} from 'carbon-components-react';
import { FormError } from 'components';
import { FormikTouched, FormikErrors } from 'formik';
import Utils from 'utilities/helper.util';

const AutoRenewForm: React.FC<Props> = ({
  touched,
  errors,
  values,
  enquiryAutoRenewPropertiesData,
  fetchingEnquiryAutoRenewProperties,
  handleSubmit,
  setFieldValue,
  setFieldTouched,
  calendarRef,
  isAnonymousView,
  onClose,
  editingEnquiryAutoRenew,
  savedSuccess,
  hideSummary,
  isNewEnquiry,
  submitLabel = 'Save auto-renew',
  cancelLabel = 'Back',
  backLabel = 'Back',
  isNewAutoRenew = false
}) => {
  const handleToggle = (val: any) => {
    setFieldValue('autoRenew', val);
    if (val && isNewAutoRenew && !values.autoRenewEvery) {
      setFieldValue('autoRenewEvery', 3);
    }
  };

  const handleNumberInputChange = (event: any) => {
    const newValue = Number(event.imaginaryTarget.value);
    setFieldValue('autoRenewEvery', newValue);
  };

  const handleDateChange = (key: string, value: any) => {
    if (Array.isArray(value) && value.length > 0) {
      const updatedValue = value[0];
      if (key === 'jobEndDate') {
        const currentRenewUntilDateObj = Utils.getNewDate(
          values.autoRenewUntil
        );
        const newJobEndDateObj = Utils.getNewDate(updatedValue);
        if (newJobEndDateObj < currentRenewUntilDateObj) {
          setFieldValue('autoRenewUntil', '');
          setFieldTouched('autoRenewUntil', true, true);
        }
      }
      const dateToSet = Utils.getNewDate(updatedValue).format();
      setFieldValue(key, dateToSet);
      setFieldTouched(key, true, true);
    }
  };

  const onChangeDate = (key: string, val: any, setField?: boolean) => {
    setField && setFieldValue(key, Utils.getNewDate(val[0]), true);
    handleDateChange(key, val);
  };

  const onCalendarClose = (key: string, val: any) => {
    setFieldValue(key, val[0]);
    setFieldTouched(key, true);
  };

  let renewUntilMinDate = Utils.toLocalDateFormat(Utils.getNewDate());
  if (enquiryAutoRenewPropertiesData?.jobStartAt) {
    renewUntilMinDate = Utils.toLocalDateFormat(
      Utils.getNewDate(enquiryAutoRenewPropertiesData?.enquiryLodgedAt)
        .add(28, 'day')
        .subtract(values.autoRenewEvery, 'day')
    );
  }
  const renewUntilMaxDate = Utils.toLocalDateFormat(
    values?.jobEndDate
      ? values.jobEndDate
      : enquiryAutoRenewPropertiesData?.jobEndAt
  );

  const jobEndMinDate = Utils.toLocalDateFormat(
    Utils.getNewDate(enquiryAutoRenewPropertiesData.jobStartAt).startOf('day')
  );
  const jobEndMaxDate = Utils.toLocalDateFormat(
    Utils.getNewDate(enquiryAutoRenewPropertiesData.jobStartAt)
      .add(2, 'year')
      .endOf('day')
  );

  const NEXT_28_DAYS_DATE_FROM_LODGE_DATE: any = Utils.getNewDate(
    enquiryAutoRenewPropertiesData.enquiryLodgedAt
  )
    .add(28, 'day')
    .endOf('day');

  return (
    <div className="auto-renew-viewer-container">
      {enquiryAutoRenewPropertiesData && !fetchingEnquiryAutoRenewProperties && (
        <>
          <div className="auto-renew-viewer-properties">
            <div className="auto-renew-viewer-properties-title">
              {!isNewAutoRenew ? 'Auto-renew properties' : 'Enable auto-renew'}
            </div>
            <Form className="modal-form--wrapper" onSubmit={handleSubmit}>
              <div className="auto-renew-viewer-properties-form-1">
                <div className="toggle-wrapper">
                  <div className="form-label">Auto-renew</div>
                  <div
                    className={`toggle-container${
                      enquiryAutoRenewPropertiesData.isEditable
                        ? '-active'
                        : '-disable'
                    }`}
                  >
                    <Toggle
                      id={'toggleAutoRenew'}
                      toggled={
                        enquiryAutoRenewPropertiesData.isEditable
                          ? values.autoRenew
                          : true
                      }
                      disabled={!enquiryAutoRenewPropertiesData.isEditable}
                      onToggle={handleToggle}
                    />
                  </div>
                </div>
                <div className="renew-until-wrapper">
                  <div className="form-label">Auto-renew until</div>
                  <div
                    className={`renew-until-container ${
                      enquiryAutoRenewPropertiesData.isEditable
                        ? ''
                        : 'remove-svg'
                    }`}
                  >
                    {enquiryAutoRenewPropertiesData.isEditable &&
                    values.autoRenew ? (
                      <DatePicker
                        dateFormat="d/m/Y"
                        datePickerType="single"
                        value={
                          values.autoRenewUntil
                            ? Utils.toLocalDateFormat(values.autoRenewUntil)
                            : ''
                        }
                        onChange={(val: any) => {
                          onChangeDate('autoRenewUntil', val, false);
                        }}
                        onClose={(val) =>
                          onCalendarClose('autoRenewUntil', val)
                        }
                        minDate={renewUntilMinDate}
                        maxDate={renewUntilMaxDate}
                        ref={calendarRef}
                      >
                        <DatePickerInput
                          id="autoRenewUntil"
                          name="autoRenewUntil"
                          data-testid="autoRenewUntil"
                          labelText=""
                          placeholder="dd/mm/yyyy"
                          size={'md'}
                          type="text"
                          autoComplete="off"
                          disabled={
                            !values.jobEndDate ||
                            !Utils.getNewDate(values.jobEndDate).isAfter(
                              NEXT_28_DAYS_DATE_FROM_LODGE_DATE,
                              'date'
                            )
                          }
                        />
                      </DatePicker>
                    ) : (
                      <TextInput
                        id="autoRenewUntil"
                        name="autoRenewUntil"
                        labelText=""
                        value={
                          enquiryAutoRenewPropertiesData.autoRenewUntil
                            ? Utils.toLocalDateFormat(
                                enquiryAutoRenewPropertiesData.autoRenewUntil
                              )
                            : ''
                        }
                        readOnly={true}
                      />
                    )}
                    {errors.autoRenewUntil && touched.autoRenewUntil && (
                      <FormError>{errors.autoRenewUntil}</FormError>
                    )}
                  </div>
                </div>
              </div>
              {!isAnonymousView && (
                <>
                  <div className="inner-divider"></div>
                  <div className="auto-renew-viewer-properties-form-2">
                    <div className="job-start-wrapper remove-svg">
                      <div>
                        <TextInput
                          id="jobStartDate"
                          name="jobStartDate"
                          labelText="Job start date"
                          value={
                            enquiryAutoRenewPropertiesData.jobStartAt
                              ? Utils.toLocalDateFormat(
                                  enquiryAutoRenewPropertiesData.jobStartAt
                                )
                              : ''
                          }
                          readOnly={true}
                        />
                      </div>
                    </div>
                    <div
                      className={`job-end-wrapper ${
                        !enquiryAutoRenewPropertiesData.isEditable
                          ? 'remove-svg'
                          : ''
                      } `}
                    >
                      <div>
                        {enquiryAutoRenewPropertiesData.isEditable &&
                        values.autoRenew ? (
                          <DatePicker
                            dateFormat="d/m/Y"
                            datePickerType="single"
                            className="custom-datepicker-style"
                            value={
                              values.jobEndDate
                                ? Utils.toLocalDateFormat(values.jobEndDate)
                                : ''
                            }
                            onChange={(val: any) => {
                              onChangeDate('jobEndDate', val, false);
                            }}
                            onClose={(val) =>
                              onCalendarClose('jobEndDate', val)
                            }
                            minDate={jobEndMinDate}
                            maxDate={jobEndMaxDate}
                            ref={calendarRef}
                          >
                            <DatePickerInput
                              id="jobEndDate"
                              name="jobEndDate"
                              data-testid="jobEndDate"
                              labelText="Job end date"
                              placeholder="dd/mm/yyyy"
                              size={'md'}
                              type="text"
                              autoComplete="off"
                            />
                          </DatePicker>
                        ) : (
                          <TextInput
                            id="jobEndDate"
                            name="jobEndDate"
                            labelText="Job end date"
                            value={
                              enquiryAutoRenewPropertiesData.jobStartAt
                                ? Utils.toLocalDateFormat(
                                    enquiryAutoRenewPropertiesData.jobEndAt
                                  )
                                : ''
                            }
                            readOnly={true}
                          />
                        )}
                        {errors.jobEndDate && touched.jobEndDate && (
                          <FormError>{errors.jobEndDate}</FormError>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
              <div className="inner-divider"></div>
              <div className="auto-renew-viewer-properties-form-3">
                <NumberInput
                  id="renew-before-days"
                  label="Auto-renew this many days before expiry"
                  invalidText="Must be within 1-7 days"
                  disabled={
                    !enquiryAutoRenewPropertiesData.isEditable ||
                    !values.autoRenew
                  }
                  max={7}
                  min={
                    (enquiryAutoRenewPropertiesData.autoRenewEvery &&
                      enquiryAutoRenewPropertiesData.isEditable) ||
                    (values.autoRenew && !isAnonymousView)
                      ? 1
                      : 0
                  }
                  step={1}
                  value={
                    isAnonymousView
                      ? !enquiryAutoRenewPropertiesData.autoRenewEvery
                        ? 0
                        : enquiryAutoRenewPropertiesData.autoRenewEvery
                      : !values.autoRenewEvery
                      ? 0
                      : values.autoRenewEvery
                  }
                  onChange={handleNumberInputChange}
                />
              </div>
              <div className="inner-divider"></div>
              <div className="auto-renew-viewer-properties-buttons">
                <Button
                  id="cancelAutoRenew"
                  name="cancelAutoRenew"
                  kind="tertiary"
                  size="sm"
                  onClick={() => onClose()}
                >
                  {enquiryAutoRenewPropertiesData.isEditable
                    ? cancelLabel
                    : backLabel}
                </Button>
                {enquiryAutoRenewPropertiesData.isEditable && (
                  <Button
                    id="submitAutoRenew"
                    name="submitAutoRenew"
                    kind="secondary"
                    size="sm"
                    type="submit"
                    data-testid="submitAutoRenew"
                    disabled={
                      editingEnquiryAutoRenew ||
                      savedSuccess ||
                      !Utils.isObjectEmpty(errors)
                    }
                  >
                    {!editingEnquiryAutoRenew && !savedSuccess ? (
                      submitLabel
                    ) : (
                      <InlineLoading
                        description={
                          !editingEnquiryAutoRenew ? 'Saved' : 'Saving...'
                        }
                        status={
                          !editingEnquiryAutoRenew ? 'finished' : 'active'
                        }
                        aria-live={'assertive'}
                        successDelay={1500}
                      />
                    )}
                  </Button>
                )}
              </div>
            </Form>
          </div>
          {!hideSummary && (
            <div className="auto-renew-viewer-summary">
              {!isNewAutoRenew && (
                <div className="auto-renew-viewer-summary-title">Summary</div>
              )}
              {enquiryAutoRenewPropertiesData.renewalsLeft > 0 &&
              !isNewAutoRenew ? (
                <>
                  <div className="auto-renew-viewer-summary-wrapper">
                    <div className="summary-label">Next renewal</div>
                    &nbsp;
                    <div className="summary-content">
                      {enquiryAutoRenewPropertiesData.nextRenewal}
                    </div>
                  </div>
                  <div className="auto-renew-viewer-summary-wrapper">
                    <div className="summary-label">Renews in</div>
                    &nbsp;
                    <div className="summary-content">
                      {enquiryAutoRenewPropertiesData.renews}
                    </div>
                  </div>
                  <div className="auto-renew-viewer-summary-wrapper">
                    <div className="summary-label">Renewals left</div>
                    &nbsp;
                    <div className="summary-content">
                      {enquiryAutoRenewPropertiesData.renewalsLeft}
                    </div>
                  </div>
                </>
              ) : isNewAutoRenew ? (
                <div className="auto-renew-viewer-hint">
                  <span style={{ fontWeight: 'bold', marginRight: '0.125rem' }}>
                    Hint
                  </span>
                  To save auto-renew properties, ensure the Job end date is more
                  than 28 days after the enquiry lodge date.
                </div>
              ) : (
                <div className="auto-renew-viewer-summary-wrapper">
                  <div className="summary-label">
                    This auto-renew series is completed
                  </div>
                </div>
              )}
            </div>
          )}

          {(isNewAutoRenew ||
            enquiryAutoRenewPropertiesData.renewalsLeft > 0) && (
            <div className="auto-renew-viewer-footer">
              <span style={{ fontWeight: 'bold' }}>About auto-renew</span>
              {enquiryAutoRenewPropertiesData.isEditable
                ? ` For longer jobs you have the option to automatically renew and get fresh plans every 28 days. 
            The maximum auto-renew period is 24 months from ${
              isNewEnquiry ? 'today' : 'the lodge date'
            } (a new enquiry is needed after that). 
            Set a shorter period to avoid receiving plans you won't need. 
            To turn off Auto-renew, set the switch to Off.`
                : ` Longer jobs have the option to automatically renew and get fresh plans every 28 days. 
            The maximum auto-renew period is 24 months from ${
              isNewEnquiry ? 'today' : 'the lodge date'
            }.`}
            </div>
          )}
        </>
      )}
      {(!enquiryAutoRenewPropertiesData ||
        fetchingEnquiryAutoRenewProperties) && (
        <div className="loading-container">
          <Loading withOverlay={false} />
        </div>
      )}
    </div>
  );
};

type Props = {
  touched: FormikTouched<any>;
  errors: FormikErrors<any>;
  values: any;
  enquiryAutoRenewPropertiesData?: any;
  fetchingEnquiryAutoRenewProperties?: boolean;
  handleSubmit: (e: React.FormEvent<HTMLFormElement> | undefined) => void;
  setFieldValue: Function;
  setFieldTouched: Function;
  calendarRef: any;
  isAnonymousView?: boolean;
  onClose: Function;
  editingEnquiryAutoRenew?: boolean;
  savedSuccess?: boolean;
  hideSummary?: boolean;
  isNewEnquiry?: boolean;
  submitLabel?: string;
  cancelLabel?: string;
  backLabel?: string;
  isExpired?: boolean;
  isNewAutoRenew?: boolean;
};

export default React.memo(AutoRenewForm);
